import { useRef, useInsertionEffect, useCallback } from 'react';
/**
 * Polyfill for the upcoming `useEffectEvent` hook from React 19.
 * @see https://react.dev/reference/react/useEffectEvent
 *
 * Returns a function reference that always calls the latest version of
 * the provided callback. This lets you read the "current" value of props or
 * state inside Effects without adding them to the dependency array, avoiding
 * unnecessary re-fires while still seeing up-to-date values.
 *
 * Replace usages with the native `useEffectEvent` once we upgrade to React 19.
 */
export function useEffectEvent(callback) {
  const ref = useRef(null);

  // useInsertionEffect runs before useLayoutEffect and useEffect, making it the
  // earliest point in the React lifecycle to update the ref. This ensures the
  // ref is always current before any layout or passive effects run, preventing
  // a stale-closure window where calling the returned function could invoke an
  // outdated callback.
  useInsertionEffect(() => {
    ref.current = callback;
  }, [callback]);
  return useCallback((...args) => {
    const fn = ref.current;
    if (!fn) {
      throw new Error("A function wrapped in useEffectEvent can't be called during rendering.");
    }
    return fn(...args);
  }, []);
}